import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControlLabel, Autocomplete,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchHealthCheckupList,
  editHealthCheckup,
  updateHealthCheckup,
  trashHealthCheckup,
  fetchSpecialtiesList,
  fetchDoctorsList,
  fetchHealthCheckupPackageList,
  fetchHealthCheckupMasterTest,
  fetchHealthCheckupTestInclusion,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";

const groupTestMasters = (raw = []) => {
  const aggregated = {};
  raw.forEach((tm) => {
    if (!aggregated[tm.test_master_id]) {
      aggregated[tm.test_master_id] = {
        test_master_id: tm.test_master_id,
        test_master_name: tm.test_master_name,
        test_inclusion_ids: [],
      };
    }
    (tm.test_inclusion || []).forEach((inc) => {
      aggregated[tm.test_master_id].test_inclusion_ids.push(inc.test_inclusion_id);
    });
  });
  return Object.values(aggregated);
};

const ListHealthCheckup = () => {
  const [healthCheckups, setHealthCheckups] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedImages, setSelectedImages] = useState({});
  const [specialities, setSpecialities] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [packages, setPackages] = useState([]);
  /** Test‑master related state */
  const [testMasters, setTestMasters] = useState([]);
  const [selectedTestMasters, setSelectedTestMasters] = useState([]);
  const [testMasterInclusions, setTestMasterInclusions] = useState({});

  useEffect(() => {
    // const loadTestMasterData = async () => {
    //   if (!editItem || !editItem.test_master_inclusions) return;

    //   const selectedMasters = [];
    //   const inclusionsMap = {};

    //   for (const tm of editItem.test_master_inclusions) {
    //     const testMaster = testMasters.find((t) => t.id === tm.test_master_id);
    //     if (testMaster) selectedMasters.push(testMaster);

    //     const inclusionRes = await fetchHealthCheckupTestInclusion(tm.test_master_id);
    //     if (inclusionRes?.data) {
    //       inclusionsMap[tm.test_master_id] = inclusionRes.data.map((inc) => ({
    //         ...inc,
    //         checked: tm.test_inclusion_ids.includes(inc.id),
    //       }));
    //     }
    //   }

    //   setSelectedTestMasters(selectedMasters);
    //   setTestMasterInclusions(inclusionsMap);
    // };
    const loadTestMasterData = async () => {
      if (!editItem || !editItem.test_master || !Array.isArray(editItem.test_master)) return;

      const selectedMasters = [];
      const inclusionsMap = {};

      const testMasterMap = new Map();
      (editItem.test_master || []).forEach((tm) => {
        const id = tm.test_master_id;
        if (!testMasterMap.has(id)) {
          testMasterMap.set(id, {
            test_master_id: id,
            test_master_name: tm.test_master_name,
            selected_ids: [],
          });
        }
        (tm.test_inclusion || []).forEach((inc) => {
          testMasterMap.get(id).selected_ids.push(inc.test_inclusion_id);
        });
      });

      for (const [id, data] of testMasterMap) {
        const master = testMasters.find((t) => t.id === id);
        if (master) selectedMasters.push(master);

        const inclusionRes = await fetchHealthCheckupTestInclusion(id);
        const selectedIds = data.selected_ids;

        inclusionsMap[id] = (inclusionRes?.data || []).map((inc) => ({
          ...inc,
          checked: selectedIds.includes(inc.id),
        }));
      }

      setSelectedTestMasters(selectedMasters);
      setTestMasterInclusions(inclusionsMap);
    };

    if (editItem) {
      loadTestMasterData();
    }
  }, [editItem, testMasters]);

  // Fetch the list of health checkups
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchHealthCheckupList();
        setHealthCheckups((response.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
        );
      } catch (err) {
        toast.error("Failed to fetch health checkups.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, "") // remove special characters
      .replace(/\s+/g, "-");       // replace spaces with hyphens
  };

  const handleInclusionChange = (masterId, inclusionId, checked) => {
    const current = editItem.test_master_inclusions || {};
    const currentIds = current[masterId] || [];

    let updatedIds;
    if (checked) {
      updatedIds = [...currentIds, inclusionId];
    } else {
      updatedIds = currentIds.filter((id) => id !== inclusionId);
    }

    setEditItem({
      ...editItem,
      test_master_inclusions: {
        ...current,
        [masterId]: updatedIds,
      },
    });
  };

  const handleEdit = async (id) => {
    try {
      // Fetch basic data in parallel
      const [editRes, specRes, docRes, pkgRes, masterRes] = await Promise.all([
        editHealthCheckup(id),
        fetchSpecialtiesList(),
        fetchDoctorsList(),
        fetchHealthCheckupPackageList(),
        fetchHealthCheckupMasterTest(),
      ]);

      const item = editRes.data[0];
      setEditItem(item);
      setTestMasters(masterRes.data);

      /* 2. Build ☑ inclusions from payload we just fetched */
      const grouped = groupTestMasters(item.test_master || []);
      const selMasters = grouped.map((g) => {
        const match = masterRes.data.find((m) => m.id === g.test_master_id);
        return (
          match || {
            id: `new-${Date.now()}`,               // free-solo master
            test_name: g.test_master_name,
          }
        );
      });

      /* 3. Fetch ALL inclusion lists in parallel and flag checked ones */
      const incMap = {};
      await Promise.all(
        selMasters.map(async (m) => {
          /* free-solo masters have no id to fetch */
          if (String(m.id).startsWith("new-")) {
            incMap[m.id] = [];
            return;
          }
          const incRes = await fetchHealthCheckupTestInclusion(m.id);
          const chosen = grouped.find((g) => g.test_master_id === m.id)?.test_inclusion_ids || [];
          incMap[m.id] = (incRes?.data || []).map((inc) => ({
            id: inc.id ?? inc.test_inclusion_id,
            inclusion_name: inc.inclusion_name ?? inc.test_inclusion_names,
            checked: chosen.includes(inc.id ?? inc.test_inclusion_id),
          }));
        })
      );

      setTestMasters(masterRes.data);
      setSelectedTestMasters(selMasters);
      setTestMasterInclusions(incMap);
    }
    catch (err) {
      toast.error("Failed to load data for editing.");
    }
  };

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImages((prev) => ({ ...prev, [fieldName]: file }));
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);

    const formData = new FormData();

    // Text fields
    const fields = [
      "health_checkup_name",
      "slug",
      "doctor_id",
      "speciality_id",
      "health_checkup_package_id",
      "checkup_title",
      "checkup_fees",
      "email",
      "phone",
      "contact_person",
      "seo_title",
      "meta_title",
      "meta_description",
      "meta_keywords",
      "custom_scripts",
      "og_title",
      "og_description",
      "og_type",
      "og_url",
      "og_locale",
      "og_site_name",
      "og_image_width",
      "og_image_height",
      "og_image_type",
      "article_modified_time",
      "canonical_link",
      "twitter_card",
      "twitter_title",
      "twitter_description",
      "twitter_site",
    ];

    fields.forEach((field) => {
      if (editItem[field] !== undefined && editItem[field] !== null) {
        formData.append(field, editItem[field]);
      }
    });

    ["checkup_image", "banner_img", "og_image", "twitter_image"].forEach((key) => {
      if (selectedImages[key]) {
        // Only append if a new file is selected
        formData.append(key, selectedImages[key]);
      }
    });

    // File/image uploads
    Object.entries(selectedImages).forEach(([key, file]) => {
      formData.append(key, file); // keys like checkup_image, banner_img, etc.
    });

    // const formattedTestMasters = selectedTestMasters.map((tm) => {
    //   if (tm.id) {
    //     return {
    //       test_master_id: tm.id,
    //       test_inclusion_ids: (testMasterInclusions[tm.id] || [])
    //         .filter((inc) => inc.checked)
    //         .map((inc) => inc.id),
    //     };
    //   } else {
    //     return {
    //       test_master_name: tm.test_master_name,
    //       test_inclusion_names: tm.test_inclusion_names || [],
    //     };
    //   }
    // });
    const formatted = selectedTestMasters.map((m) => {
      const arr = testMasterInclusions[m.id] || [];

      const existingIds = arr
        .filter((i) => !String(i.id).startsWith("new-") && i.checked)
        .map((i) => i.id);

      const newNames = arr
        .filter((i) => String(i.id).startsWith("new-") && i.checked)
        .map((i) => i.inclusion_name);

      if (String(m.id).startsWith("new-")) {
        /* brand-new master */
        return {
          test_master_name: m.test_name,
          test_inclusion_names: newNames,
        };
      }

      /* existing master – may have BOTH ids and fresh names */
      const obj = { test_master_id: m.id };
      if (existingIds.length) obj.test_inclusion_ids = existingIds;
      if (newNames.length) obj.test_inclusion_names = newNames;
      return obj;
    });
    formData.append("test_master_inclusions", JSON.stringify(formatted));

    try {
      const response = await updateHealthCheckup(editItem.checkup_id, formData);
      toast.success("Health Checkup updated successfully!");
      setHealthCheckups((prev) =>
        prev.map((item) =>
          item.checkup_id === editItem.checkup_id ? { ...item, ...response.data } : item
        )
      );
      setEditItem(null);
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to save changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      await trashHealthCheckup(id);
      toast.success("Health Checkup deleted successfully!");
      setHealthCheckups((prev) =>
        prev.filter((item) => item.checkup_id !== id)
      );
      setDeleteItemId(null); // Close the delete confirmation dialog
    } catch (err) {
      toast.error("Failed to delete health checkup.");
    } finally {
      setLoadingAction(false);
    }
  };

  // Add New Test Inclusion
  const addNewInclusion = (masterId, name) => {
    if (!name.trim()) return;
    const newInc = {
      id: `new-${Date.now()}`,
      inclusion_name: name,
      checked: true,
    };
    setTestMasterInclusions((prev) => ({
      ...prev,
      [masterId]: [...(prev[masterId] || []), newInc],
    }));
  };

  const columns = [
    { field: "checkup_id", headerName: "ID", width: 100 },
    { field: "health_checkup_name", headerName: "Health Package Name", width: 200 },
    { field: "checkup_fees", headerName: "Fees", width: 150 },
    { field: "checkup_title", headerName: "Title", width: 200 },
    // { field: "contact_person", headerName: "Contact Person", width: 200 },
    // { field: "email", headerName: "Email", width: 200 },
    // { field: "phone", headerName: "Phone", width: 150 },
    {
      field: "checkup_image",
      headerName: "Image",
      width: 150,
      renderCell: (params) =>
        params.row.checkup_image ? (
          <img
            src={`${baseImage_path}/${params.row.checkup_image.replace(
              /^\.?\//,
              ""
            )}`}
            alt="Checkup"
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 4,
            }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.checkup_id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteItemId(params.row.checkup_id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Health Checkup List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={healthCheckups}
          columns={columns}
          autoHeight
          getRowId={(row) => row.checkup_id} // Map `checkup_id` as the unique row ID
        />
      )}

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Health Checkup</DialogTitle>
          <DialogContent>
            <TextField fullWidth margin="normal" label="Package Name" value={editItem.health_checkup_name || ""} onChange={(e) => setEditItem({ ...editItem, health_checkup_name: e.target.value, slug: generateSlug(e.target.value) })} />

            <TextField fullWidth margin="normal" label="Slug" value={editItem.slug || ""} onChange={(e) => setEditItem({ ...editItem, slug: e.target.value })} />

            <TextField fullWidth margin="normal" label="Title" value={editItem.checkup_title || ""} onChange={(e) => setEditItem({ ...editItem, checkup_title: e.target.value })} />
            <TextField fullWidth margin="normal" label="Fees" value={editItem.checkup_fees || ""} onChange={(e) => setEditItem({ ...editItem, checkup_fees: e.target.value })} />
            <TextField fullWidth margin="normal" label="Email" value={editItem.email || ""} onChange={(e) => setEditItem({ ...editItem, email: e.target.value })} />
            <TextField fullWidth margin="normal" label="Phone" value={editItem.phone || ""} onChange={(e) => setEditItem({ ...editItem, phone: e.target.value })} />
            <TextField fullWidth margin="normal" label="Contact Person" value={editItem.contact_person || ""} onChange={(e) => setEditItem({ ...editItem, contact_person: e.target.value })} />

            {/* <Autocomplete
              multiple
              options={testMasters}
              getOptionLabel={(option) => option.test_name}
              value={testMasters.filter(tm =>
                selectedTestMasters.find(sel => sel.id === tm.id)
              )}
              onChange={async (e, value) => {
                setSelectedTestMasters(value);
                const updatedInclusions = {};
                for (const master of value) {
                  if (!testMasterInclusions[master.id]) {
                    const incRes = await fetchHealthCheckupTestInclusion(master.id);
                    updatedInclusions[master.id] = (incRes?.data || []).map((inc) => ({
                      ...inc,
                      checked: false
                    }));
                  } else {
                    updatedInclusions[master.id] = testMasterInclusions[master.id];
                  }
                }
                setTestMasterInclusions((prev) => ({ ...prev, ...updatedInclusions }));
              }}
              renderInput={(params) => <TextField {...params} label="Select Test Masters" fullWidth />}
            />

            {selectedTestMasters.map((master) => (
              <Box key={master.id} mt={2}>
                <Typography variant="subtitle1">{master.test_name}</Typography>
                {testMasterInclusions[master.id]?.map((inc) => (
                  <FormControlLabel
                    key={inc.id}
                    control={
                      <Checkbox
                        checked={inc.checked}
                        onChange={(e) => {
                          setTestMasterInclusions((prev) => ({
                            ...prev,
                            [master.id]: prev[master.id].map((i) =>
                              i.id === inc.id ? { ...i, checked: e.target.checked } : i
                            ),
                          }));
                        }}
                      />
                    }
                    label={inc.inclusion_name}
                  />
                ))}
              </Box>
            ))} */}

            {/* ========= Test Masters ========= */}
            <Autocomplete
              multiple
              freeSolo
              options={testMasters}
              getOptionLabel={(opt) => opt.test_name || ""}
              value={selectedTestMasters}
              onChange={(e, value, reason, details) => {
                // When user types a new entry, MUI puts plain string in `details.option`
                if (typeof details?.option === "string") {
                  const newObj = {
                    id: `new-${Date.now()}`,
                    test_name: details.option,
                  };
                  value = [...value.filter((v) => v.id !== newObj.id), newObj];
                  setTestMasters((prev) => [...prev, newObj]);
                }
                setSelectedTestMasters(value);
                // ensure inclusion map has entry
                value.forEach((m) => {
                  if (!testMasterInclusions[m.id]) {
                    setTestMasterInclusions((prev) => ({ ...prev, [m.id]: [] }));
                  }
                });
              }}
              renderInput={(params) => <TextField {...params} label="Test Masters" margin="normal" />}
            />

            {selectedTestMasters.map((m) => (
              <Box key={m.id} mt={2} p={1} border={1} borderColor="grey.300" borderRadius={1}>
                <Typography variant="subtitle1" gutterBottom>
                  {m.test_name}
                </Typography>
                {(testMasterInclusions[m.id] || []).map((inc) => (
                  <FormControlLabel
                    key={inc.id}
                    control={
                      <Checkbox
                        checked={inc.checked}
                        onChange={(e) => {
                          setTestMasterInclusions((prev) => ({
                            ...prev,
                            [m.id]: prev[m.id].map((i) =>
                              i.id === inc.id ? { ...i, checked: e.target.checked } : i
                            ),
                          }));
                        }}
                      />
                    }
                    label={inc.inclusion_name}
                  />
                ))}
                {/* Add new inclusion */}
                <Box display="flex" mt={1}>
                  <TextField
                    size="small"
                    placeholder="Add inclusion"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addNewInclusion(m.id, e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                  <Button
                    sx={{ ml: 1 }}
                    onClick={(e) => {
                      const input = e.currentTarget.previousSibling.querySelector("input");
                      addNewInclusion(m.id, input.value);
                      input.value = "";
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            ))}

            {/* SEO */}
            < TextField fullWidth margin="normal" label="SEO Title" value={editItem.seo_title || ""} onChange={(e) => setEditItem({ ...editItem, seo_title: e.target.value })} />
            <TextField fullWidth margin="normal" label="Meta Title" value={editItem.meta_title || ""} onChange={(e) => setEditItem({ ...editItem, meta_title: e.target.value })} />
            <TextField fullWidth margin="normal" label="Meta Description" value={editItem.meta_description || ""} onChange={(e) => setEditItem({ ...editItem, meta_description: e.target.value })} />
            <TextField fullWidth margin="normal" label="Meta Keywords" value={editItem.meta_keywords || ""} onChange={(e) => setEditItem({ ...editItem, meta_keywords: e.target.value })} />
            <TextField fullWidth margin="normal" label="Custom Scripts" value={editItem.custom_scripts || ""} onChange={(e) => setEditItem({ ...editItem, custom_scripts: e.target.value })} />

            {/* OG & Twitter */}
            <TextField fullWidth margin="normal" label="OG Title" value={editItem.og_title || ""} onChange={(e) => setEditItem({ ...editItem, og_title: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Description" value={editItem.og_description || ""} onChange={(e) => setEditItem({ ...editItem, og_description: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Type" value={editItem.og_type || ""} onChange={(e) => setEditItem({ ...editItem, og_type: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG URL" value={editItem.og_url || ""} onChange={(e) => setEditItem({ ...editItem, og_url: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Locale" value={editItem.og_locale || ""} onChange={(e) => setEditItem({ ...editItem, og_locale: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Site Name" value={editItem.og_site_name || ""} onChange={(e) => setEditItem({ ...editItem, og_site_name: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Image Width" value={editItem.og_image_width || ""} onChange={(e) => setEditItem({ ...editItem, og_image_width: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Image Height" value={editItem.og_image_height || ""} onChange={(e) => setEditItem({ ...editItem, og_image_height: e.target.value })} />
            <TextField fullWidth margin="normal" label="OG Image Type" value={editItem.og_image_type || ""} onChange={(e) => setEditItem({ ...editItem, og_image_type: e.target.value })} />
            <TextField fullWidth margin="normal" label="Article Modified Time" value={editItem.article_modified_time || ""} onChange={(e) => setEditItem({ ...editItem, article_modified_time: e.target.value })} />
            <TextField fullWidth margin="normal" label="Canonical Link" value={editItem.canonical_link || ""} onChange={(e) => setEditItem({ ...editItem, canonical_link: e.target.value })} />
            <TextField fullWidth margin="normal" label="Twitter Card" value={editItem.twitter_card || ""} onChange={(e) => setEditItem({ ...editItem, twitter_card: e.target.value })} />
            <TextField fullWidth margin="normal" label="Twitter Title" value={editItem.twitter_title || ""} onChange={(e) => setEditItem({ ...editItem, twitter_title: e.target.value })} />
            <TextField fullWidth margin="normal" label="Twitter Description" value={editItem.twitter_description || ""} onChange={(e) => setEditItem({ ...editItem, twitter_description: e.target.value })} />
            <TextField fullWidth margin="normal" label="Twitter Site" value={editItem.twitter_site || ""} onChange={(e) => setEditItem({ ...editItem, twitter_site: e.target.value })} />

            {/* Images */}
            {["checkup_image", "banner_img", "og_image", "twitter_image"].map((key) => (
              <Box key={key} mt={2}>
                <Typography variant="subtitle1">{key.replace("_", " ").toUpperCase()}</Typography>
                {editItem[key] && (
                  <img src={`${baseImage_path}/${editItem[key]}`} alt={key} style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 4 }} />
                )}
                <input type="file" onChange={(e) => handleImageChange(e, key)} accept="image/*" />
              </Box>
            ))}

            {/* Test Master Inclusions */}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setEditItem(null)} variant="outlined" style={{ fontWeight: "bold", backgroundColor: "grey" }}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveEdit} disabled={loadingAction}>
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteItemId && (
        <Dialog open onClose={() => setDeleteItemId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this health checkup?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteItemId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteItemId)}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ListHealthCheckup;
